import Console from 'utility/Debug/Console';

import {
    EventConfig,
    EventConfigDefinition,
    PENDING_STATES,
    PollEventData,
    PollingData,
    PollingEventType,
    PollingStatus,
    PreviousEventDataMap,
} from '../types';

import { callSafe } from './callbacks';

/**
 * calls the onPollUpdate callback for the given event type if the updatePending flag is set
 */
export const processPendingUpdate = (
    config: EventConfigDefinition,
    previousData: PollEventData | undefined
) => {
    if (previousData?.updatePending) {
        Console.info('pending update, trigger final update');
        callSafe(config.onPollFinished, previousData.event);
    }
};

interface EventProcessingData {
    data: PollEventData;
    previousData: PollEventData | undefined;
    config: EventConfigDefinition;
    timePassed: number;
}

/**
 * processes a single event from poll response
 *
 * @return whether polling is still necessary
 */
const processEvent = ({
    data,
    previousData,
    config,
    timePassed,
}: EventProcessingData): boolean => {
    if (config.timeout && timePassed > config.timeout) {
        // event timeout reached -> notify event if still pending
        if (PENDING_STATES.has(data.event.status)) {
            Console.info('event poll timeout reached:', {
                timeout: config.timeout,
                timePassed,
            });
            processPendingUpdate(config, previousData);
            callSafe(config.onTimeout, data.updatePending);
        }
        return false;
    }
    switch (data.event.status) {
        case PollingStatus.Finished:
            if (
                previousData?.event.status !== PollingStatus.Finished ||
                previousData?.event.total !== data.event.total
            ) {
                Console.info(
                    'notify onPollFinished for event:',
                    data.event.type
                );
                data.updatePending = false;
                callSafe(config.onPollFinished, data.event);
            }
            return false;
        case PollingStatus.Updated:
            const updateInterval = Math.max(config.updateInterval ?? 1, 1);
            data.updateCount = Math.floor(data.event.finished / updateInterval);
            const info = {
                finished: data.event.finished,
                previousFinished: previousData?.event.finished,
                updateInterval,
                updateCount: data.updateCount,
                previousUpdateCount: previousData?.updateCount,
            };
            if (data.updateCount > (previousData?.updateCount ?? 0)) {
                Console.info(
                    'notify onPollUpdate for event:',
                    data.event.type,
                    info
                );
                data.updatePending = false;
                callSafe(config.onPollUpdate, data.event);
            } else {
                Console.info(
                    'updateInterval not reached for event:',
                    data.event.type,
                    info
                );
                data.updatePending = true;
            }
            return true;
        case PollingStatus.Running:
            return true;
        default:
            if (
                PENDING_STATES.has(
                    previousData?.event.status ?? PollingStatus.Running
                )
            ) {
                Console.info('notify onPollError for event:', {
                    event: data.event.type,
                    status: data.event.status,
                });
                processPendingUpdate(config, previousData);
                callSafe(config.onPollError, data.updatePending);
            }
            return false;
    }
};

const ERROR_STATES = new Set([
    PollingStatus.Error,
    PollingStatus.Unknown,
    PollingStatus.AllError,
]);
const VALID_STATES = new Set(Object.values(PollingStatus));

interface ResponseProcessingData {
    responseData: PollingData;
    previousEventData: PreviousEventDataMap;
    events: EventConfig;
    notifyErrorToAllPending: () => void;
    timePassed: number;
}

/**
 * processes the given poll response
 *
 * @return whether polling is still necessary
 */
export const processResponse = ({
    responseData,
    previousEventData,
    events,
    notifyErrorToAllPending,
    timePassed,
}: ResponseProcessingData): boolean => {
    Console.info('polling response received:', responseData);
    const globalStatus = responseData.status;
    if (ERROR_STATES.has(globalStatus) || !VALID_STATES.has(globalStatus)) {
        // global polling error or invalid status -> notify all pending events
        Console.info('global poll error:', { globalStatus });
        notifyErrorToAllPending();
        return false;
    }
    const receivedEventTypes = new Set<PollingEventType>();
    let stillPending = false;
    responseData.events?.forEach((event) => {
        const eventConfig = events[event.type];
        if (!eventConfig) {
            // not configured event -> skip processing
            return;
        }
        const previousData = previousEventData.get(event.type);
        Console.info('poll event received:', {
            event,
            eventConfig,
            previousData,
        });
        const pollEventData: PollEventData = {
            event,
            updateCount: previousData?.updateCount ?? 0,
            updatePending: previousData?.updatePending ?? false,
        };
        previousEventData.set(event.type, pollEventData);
        receivedEventTypes.add(event.type);
        stillPending =
            processEvent({
                data: pollEventData,
                previousData,
                config: eventConfig,
                timePassed,
            }) || stillPending;
    });
    if (receivedEventTypes.size >= 1) {
        return stillPending;
    }
    if (globalStatus === PollingStatus.Running) {
        // no configured events on global status running is not prohibited
        return true;
    }
    Console.error('no configured event received');
    notifyErrorToAllPending();
    return false;
};
